/**
 * Company Details Tab Component
 * Camadas de destaques (StatCard) e blocos temáticos (SectionCard + InfoItem)
 * da tela de visualização da empresa.
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  ViewModule as ModulesIcon,
  AccountTree as HierarchyIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SectionCard, InfoItem, StatCard } from '../../../../components/common';
import {
  Company,
  CompanyHierarchy,
  COMPANY_TYPE_LABELS,
  COMPANY_SIZE_LABELS,
  COMPANY_SIZE_NONE_LABEL,
  COMPANY_STATUS_LABELS,
  MODULE_LABELS,
  porteAceitaDispensa,
  CompanyType,
  CompanyStatus,
  ModuleType,
  formatCNPJ,
  formatCEP,
  formatPhone,
} from '../../../../types/company';
import { companyService } from '../../../../services/companyService';

interface CompanyDetailsTabProps {
  company: Company;
}

const CompanyDetailsTab: React.FC<CompanyDetailsTabProps> = ({ company }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const activeModules = company.modules?.map((m) => m.module) ?? [];

  // Hierarchy data (loaded inline)
  const [hierarchy, setHierarchy] = useState<CompanyHierarchy | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(true);

  useEffect(() => {
    const loadHierarchy = async () => {
      setHierarchyLoading(true);
      try {
        const data = await companyService.getCompanyHierarchy(company.id.toString());
        setHierarchy(data);
      } catch {
        // Silently fail — hierarchy is supplementary info
      } finally {
        setHierarchyLoading(false);
      }
    };
    loadHierarchy();
  }, [company.id]);

  const hasParent = !!hierarchy?.parent;
  const branchCount = hierarchy?.branches?.length ?? 0;

  const companyTypeLabel = company.companyType
    ? COMPANY_TYPE_LABELS[company.companyType as CompanyType]
    : '—';

  return (
    <Box>
      {/* ═══ Destaques ═══ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<BusinessIcon fontSize="small" />}
            label="Tipo"
            value={companyTypeLabel}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<LocationIcon fontSize="small" />}
            label="Localização"
            value={
              company.city
                ? `${company.city}${company.state ? `/${company.state}` : ''}`
                : '—'
            }
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<ModulesIcon fontSize="small" />}
            label="Módulos Contratados"
            value={String(activeModules.length)}
            color="#00897B"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<StoreIcon fontSize="small" />}
            label="Filiais"
            value={hierarchyLoading ? '—' : String(branchCount)}
            color="#546E7A"
          />
        </Grid>
      </Grid>

      {/* ═══ Blocos temáticos ═══ */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Company Data */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Dados da Empresa"
            icon={<BusinessIcon fontSize="small" />}
            accentColor={theme.palette.success.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Razão Social" value={company.legalName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Nome Fantasia" value={company.tradeName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="CNPJ" value={formatCNPJ(company.cnpj)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Tipo" value={companyTypeLabel} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="Porte"
                  value={company.companySize ? COMPANY_SIZE_LABELS[company.companySize] : COMPANY_SIZE_NONE_LABEL}
                />
              </Grid>
              {porteAceitaDispensa(company.companySize) && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Dispensada do PCMSO (NR-01)"
                    value={company.pcmsoExemptNr01 ? 'Sim' : 'Não'}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="Status"
                  value={
                    <Chip
                      label={
                        COMPANY_STATUS_LABELS[company.status as CompanyStatus] ||
                        company.status
                      }
                      size="small"
                      color={
                        company.status === CompanyStatus.ACTIVE
                          ? 'success'
                          : company.status === CompanyStatus.INACTIVE
                          ? 'default'
                          : company.status === CompanyStatus.SUSPENDED
                          ? 'warning'
                          : 'error'
                      }
                    />
                  }
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Address */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Endereço"
            icon={<LocationIcon fontSize="small" />}
            accentColor={theme.palette.primary.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="CEP" value={formatCEP(company.zipCode)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Estado" value={company.state} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Cidade" value={company.city} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Bairro" value={company.neighborhood} />
              </Grid>
              <Grid size={12}>
                <InfoItem
                  label="Logradouro"
                  fullWidth
                  value={
                    company.address
                      ? `${company.address}${company.addressNumber ? `, ${company.addressNumber}` : ''}`
                      : ''
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="Código IBGE" value={company.ibgeCode} />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Registrations */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Inscrições e Registros"
            icon={<ReceiptIcon fontSize="small" />}
            accentColor="#FF8F00"
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="Inscrição Estadual"
                  value={company.stateRegistration}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="Inscrição Municipal"
                  value={company.municipalRegistration}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Contact */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Contato da Empresa"
            icon={<PhoneIcon fontSize="small" />}
            accentColor={theme.palette.primary.main}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem label="E-mail" value={company.email} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="Telefone"
                  value={company.phone ? formatPhone(company.phone) : ''}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoItem
                  label="WhatsApp"
                  value={company.whatsapp ? formatPhone(company.whatsapp) : ''}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Modules */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Módulos Contratados"
            icon={<ModulesIcon fontSize="small" />}
            accentColor="#00897B"
            badge={<Chip size="small" label={activeModules.length} />}
          >
            {activeModules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum registro encontrado
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeModules.map((module) => (
                  <Chip
                    key={module}
                    label={MODULE_LABELS[module as ModuleType]}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </SectionCard>
        </Grid>

        {/* Hierarchy (inline summary) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Hierarquia"
            icon={<HierarchyIcon fontSize="small" />}
            accentColor="#546E7A"
            badge={
              hierarchyLoading ? undefined : <Chip size="small" label={branchCount} />
            }
          >
            {hierarchyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Tipo" value={companyTypeLabel} />
                </Grid>
                {(hasParent || company.companyType === CompanyType.HEADQUARTERS) && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Matriz"
                      value={
                        hasParent ? (
                          <MuiLink
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/companies/${hierarchy!.parent!.id}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {hierarchy!.parent!.tradeName || hierarchy!.parent!.legalName}
                          </MuiLink>
                        ) : (
                          <Chip
                            label="Esta empresa é a Matriz"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )
                      }
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Filiais"
                    value={
                      branchCount > 0
                        ? `${branchCount} filial${branchCount > 1 ? 'is' : ''}`
                        : 'Nenhum registro encontrado'
                    }
                  />
                </Grid>
              </Grid>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDetailsTab;
